import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import CompleteButton from 'src/components/shared/CompleteButton';
import moment from 'moment';
import 'moment/locale/tr';

import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import { toast } from 'react-toastify';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocalization } from 'src/context/LocalizationContext';
import { StatusList } from 'src/types/statusTypes';

export default function CreateReport() {
  const { language } = useLocalization();
  const parsedUser = JSON.parse(localStorage.getItem('user'));

  const [option, setOption] = useState('created');
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [isBackdropOpen, setIsBackdropOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [pointInputValue, setPointInputValue] = useState('');
  const [pointOptions, setPointOptions] = useState([]);
  const [selectedPoiTypes, setSelectedPoiTypes] = useState([]);
  const [selectedCorporational, setSelectedCorporational] = useState('');

  const partnerList = [
    { label: 'Trendyol', value: 'trendyol' },
    { label: 'Aras Kargo', value: 'aras-kargo' },
    { label: 'Hepsiburada', value: 'hepsiburada' },
    { label: 'HepsiJET', value: 'hepsijet' },
    { label: 'Sürat Kargo', value: 'surat-kargo' },
    { label: 'CargoX', value: 'kargoist' },
    { label: 'DHL', value: 'dhl' },
    { label: 'Handsfree', value: 'handsfree' },
    { label: 'Getir', value: 'getir' },
    { label: 'Shopify', value: 'shopify' },
  ];

  const poiTypeList = [
    { label: 'EP', value: 'EP' },
    { label: 'EPS', value: 'EPS' },
    { label: 'EPX', value: 'EPX' },
  ];

  const showPoiType = !selectedPoint;
  const showCorporational =
    showPoiType &&
    selectedPoiTypes.length === 1 &&
    selectedPoiTypes[0] === 'EPS';

  useEffect(() => {
    api
      .post('flow/get-points')
      .then((response) => {
        if (response.data.status) {
          const titles = response.data.result.map((p) => ({
            id: p.id,
            title: p.title,
          }));
          const unique = titles.filter(
            (v, i, a) => a.findIndex((t) => t.title === v.title) === i
          );
          setPointOptions(unique);
        }
      })
      .catch(() => {});
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleChangeOption = (event) => {
    setOption(event.target.value);
  };

  function formatDate(date) {
    date = new Date(date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const handleCreateReport = () => {
    const filters = {};
    if (selectedPartners.length > 0) filters.apiUserType = selectedPartners;
    if (selectedStatuses.length > 0) filters.status = selectedStatuses;
    if (selectedPoint != null) filters.poiID = [String(selectedPoint)];
    if (
      showPoiType &&
      selectedPoiTypes.length > 0 &&
      selectedPoiTypes.length < poiTypeList.length
    ) {
      filters.poiType = selectedPoiTypes;
    }
    if (showCorporational && selectedCorporational !== '') {
      filters.isCorporational = selectedCorporational === 'true';
    }

    setIsBackdropOpen(true);
    Services.GetReport(
      parsedUser.token,
      option,
      formatDate(startDate),
      formatDate(endDate),
      filters
    )
      .then((response) => {
        if (response.data.status) {
          setIsBackdropOpen(false);
          window.open(response.data.result, '_blank');
        } else {
          setIsBackdropOpen(false);
          toast.error('Rapor oluşturuluracak veri mevcut değil.');
        }
      })
      .catch((error) => {
        setIsBackdropOpen(false);
        toast.error('Rapor oluşturulurken bir sorunla karşılaşıldı!');
        console.log(error);
      });
  };

  return (
    <>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1,
          flexDirection: 'column',
          display: 'flex',
          gap: '50px',
        }}
        open={isBackdropOpen}
      >
        <Typography>{language.create_report.titles.warning}</Typography>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Helmet>
        <title> {language.create_report.titles.site_title} </title>
      </Helmet>

      <Stack style={{ paddingInline: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {language.create_report.titles.title}
          </Typography>
        </Stack>
        <Card>
          <Stack
            direction="column"
            alignItems="flex-start"
            justifyContent="flex-start"
            paddingY={2}
            paddingX={2}
          >
            <Grid item md={6} xs={12}>
              <Typography variant="h6">
                {language.create_report.titles.report_type}
              </Typography>
              <RadioGroup
                sx={{ marginTop: '32px' }}
                aria-label={language.create_report.titles.report_type}
                name="option"
                value={option}
                onChange={handleChangeOption}
                row
              >
                <FormControlLabel
                  value="created"
                  control={<Radio />}
                  label={language.commons.labels.created_report}
                />
                <FormControlLabel
                  value="inBranch"
                  control={<Radio />}
                  label={language.commons.labels.inBranch_report}
                />
                <FormControlLabel
                  value="delivered"
                  control={<Radio />}
                  label={language.commons.labels.delivered_report}
                />
                <FormControlLabel
                  value="completed"
                  control={<Radio />}
                  label={language.commons.labels.completed_report}
                />
              </RadioGroup>
            </Grid>

            <Grid
              container
              spacing={2}
              sx={{ marginTop: '32px', width: '100%' }}
            >
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Partner</InputLabel>
                  <Select
                    multiple
                    value={selectedPartners}
                    label="Partner"
                    onChange={(e) => setSelectedPartners(e.target.value)}
                    renderValue={(selected) =>
                      selected
                        .map(
                          (v) => partnerList.find((p) => p.value === v)?.label
                        )
                        .join(', ')
                    }
                  >
                    {partnerList.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        <Checkbox
                          checked={selectedPartners.includes(p.value)}
                        />
                        <ListItemText primary={p.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Statü</InputLabel>
                  <Select
                    multiple
                    value={selectedStatuses}
                    label="Statü"
                    onChange={(e) => setSelectedStatuses(e.target.value)}
                    renderValue={(selected) =>
                      selected
                        .map((v) => StatusList.find((s) => s.id === v)?.label)
                        .join(', ')
                    }
                  >
                    {StatusList.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Checkbox checked={selectedStatuses.includes(s.id)} />
                        <ListItemText primary={s.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  value={
                    pointOptions.find((o) => o.id === selectedPoint) || null
                  }
                  onChange={(_e, newValue) =>
                    setSelectedPoint(newValue ? newValue.id : null)
                  }
                  inputValue={pointInputValue}
                  onInputChange={(_e, newInputValue) =>
                    setPointInputValue(newInputValue)
                  }
                  options={pointOptions}
                  getOptionLabel={(o) => o.title}
                  isOptionEqualToValue={(o, v) => o.id === v}
                  noOptionsText="Seçenek bulunamadı"
                  renderInput={(params) => (
                    <TextField {...params} label="Nokta" />
                  )}
                />
              </Grid>
              {showPoiType && (
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Nokta Tipi</InputLabel>
                    <Select
                      multiple
                      value={selectedPoiTypes}
                      label="Nokta Tipi"
                      onChange={(e) => setSelectedPoiTypes(e.target.value)}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {poiTypeList.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          <Checkbox
                            checked={selectedPoiTypes.includes(p.value)}
                          />
                          <ListItemText primary={p.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {showCorporational && (
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Bireysel / Kurumsal</InputLabel>
                    <Select
                      value={selectedCorporational}
                      label="Bireysel / Kurumsal"
                      onChange={(e) => setSelectedCorporational(e.target.value)}
                    >
                      <MenuItem value="">Tümü</MenuItem>
                      <MenuItem value="false">Bireysel</MenuItem>
                      <MenuItem value="true">Kurumsal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <Grid
              container
              alignItems="center"
              xs={8}
              spacing={2}
              sx={{ marginTop: '32px' }}
            >
              <Grid item>
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={'tr'}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    label={language.commons.labels.report_start_date}
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item>
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={'tr'}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    label={language.commons.labels.report_end_date}
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    disabled={!startDate}
                    minDate={moment(startDate)}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item>
                <CompleteButton
                  sx={{ height: '54px' }}
                  disabled={!(startDate && endDate) || startDate > endDate}
                  label={'Rapor Oluştur'}
                  onClick={handleCreateReport}
                />
                {!(startDate && endDate) || startDate > endDate ? (
                  <Tooltip title={language.commons.labels.file_download}>
                    <IconButton
                      size="small"
                      sx={{ color: 'gray', marginLeft: '8px' }}
                      onClick={handleOpen}
                    >
                      <HelpOutlineIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={language.commons.labels.file_download}>
                    <IconButton
                      size="small"
                      sx={{
                        color: 'gray',
                        marginLeft: '8px',
                        animation: 'pulseInfo 2s infinite',
                        '@keyframes pulseInfo': {
                          '0%': {
                            transform: 'scale(1)',
                            color: 'gray',
                          },
                          '50%': {
                            transform: 'scale(1.1)',
                            color: '#088AB2', // Açık mavi
                          },
                          '100%': {
                            transform: 'scale(1)',
                            color: 'gray',
                          },
                        },
                      }}
                      onClick={handleOpen}
                    >
                      <HelpOutlineIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Modal
                  open={open}
                  onClose={handleClose}
                  aria-labelledby="modal-title"
                  aria-describedby="modal-description"
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 800,
                      bgcolor: 'background.paper',
                      borderRadius: '10px',
                      boxShadow: 24,
                      p: 4,
                      justifyContent: 'flex-end',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <h3 id="modal-title">
                      {language.commons.labels.warning_file_download}
                    </h3>
                    <img
                      src={require('../../src/assets/images/popUpError.png')}
                      alt="Pop-up Error"
                      style={{
                        display: 'block',
                        margin: '20px auto',
                        maxWidth: '100%',
                        height: 'auto',
                      }}
                    />
                    <p id="modal-description">
                      {language.commons.warnings.excel_browser}
                    </p>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="primary"
                    >
                      {language.commons.okay}
                    </Button>
                  </Box>
                </Modal>
              </Grid>
            </Grid>
          </Stack>
        </Card>
      </Stack>
    </>
  );
}